import { Box, Card, CardHeader, CardContent, Typography, Tab } from "@mui/material";
import { TabContext, TabList, TabPanel } from '@mui/lab';
import React, { useContext, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { BreadcrumbContext } from '../../../../components/Breadcrumb';
import { AuthContext } from "../../../../components/FirebaseAuth";
import config from "../../../../inc/config.json";
import { useLocation } from "react-router-dom";
import { pushToDataLayer } from "../../../../libs/datalayer";

const codeBlockSx = {
    backgroundColor: "#1e2530",
    color: "#cbd5e1",
    borderRadius: "10px",
    padding: "24px",
    fontSize: "0.8em",
    whiteSpace: "pre-wrap",
};

const Installation = () => {
    const { t } = useTranslation();
    const mountedRef = useRef(true);
    const { hash } = useLocation();
    useEffect(() => {
        switch(hash){
            case "#new-free":
                pushToDataLayer({event: "subscribe", subscriptionPlan:"free", subscriptionType:"new"});
                break;
            case "#new-paid":
                pushToDataLayer({event: "subscribe", subscriptionPlan:"paid", subscriptionType:"new"});
                break;
            case "#change":
                pushToDataLayer({event: "subscribe", subscriptionPlan:"paid", subscriptionType:"change"});
                break;
            default:
                break;
        }
    },[hash]);

    const { userData } = useContext(AuthContext);
    const { setBreadcrumb } = useContext(BreadcrumbContext);

    useEffect(() => {
        setBreadcrumb([
            {
                to: "/",
                text: t('common.home'),
                active: false
            },
            {
                to: "/account/"+userData.currentAccount.id+"/",
                text: userData.currentAccount.name,
                active: false
            },
            {
                to: null,
                text: t('nav.dashboards'),
                active: true
            }
        ]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData, setBreadcrumb, t]);

    useEffect(() => {
        return () => {
            mountedRef.current = false
        }
    },[]);


    const [value, setValue] = React.useState("1");

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    return (
        <Card
            sx={{
                backgroundColor: (theme) => theme.custom.card,
                boxShadow: (theme) => theme.custom.cardShadow,
                border: (theme) => `1px solid ${theme.custom.border}`,
            }}
        >
            <CardHeader title={t('installation.title')} />
            <CardContent>
                <Box sx={{ width: '100%' }}>
                    <TabContext value={value}>
                        <Box sx={{ borderBottom: 1, borderColor: (theme) => theme.custom.border }}>
                            <TabList value={value} onChange={handleChange} aria-label="installation instructions tabs">
                                <Tab label={t('installation.tabVanilla')} value="1" />
                                <Tab label={t('installation.tabReact')} value="2" />
                                <Tab label={t('installation.tabGtm')} value="3" />
                            </TabList>
                        </Box>
                        <TabPanel value="1">
                            <Typography variant="h6" component="span">{t('installation.vanillaStep1')}</Typography>
                            <Box component="pre" sx={codeBlockSx}>
                                {
    `var VisitorAPI=function(t,e,a){var s=new XMLHttpRequest;s.onreadystatechange=function(){var t;s.readyState===XMLHttpRequest.DONE&&(200===(t=JSON.parse(s.responseText)).status?e(t.data):a(t.status,t.result))},s.open("GET","`+config.endpoint+`?pid="+t),s.send(null)};`
                                }
                            </Box>
                            <Typography variant="h6" component="span">{t('installation.step2Call')}</Typography>
                            <Typography>{t('installation.paramsIntro')}</Typography>
                            <ul>
                                <li>{t('installation.paramProjectId')} <b><i>{userData.currentAccount.id}</i></b></li>
                                <li>{t('installation.paramSuccess')}</li>
                                <li>{t('installation.paramError')}</li>
                            </ul>
                            <Typography>{t('installation.vanillaExampleIntro')}</Typography>
                            <Box component="pre" sx={codeBlockSx}>
                                {
    `VisitorAPI(
        "`+userData.currentAccount.id+`",
        function(data){console.log(data)},
        function(errorCode, errorMessage){console.log(errorCode, errorMessage)}
    );`
                                }
                            </Box>
                            <Typography>{t('installation.docsLink')} <a href="https://www.visitorapi.com/help-articles/get-started" rel="noreferrer" target="_blank">documentation</a></Typography>
                        </TabPanel>
                        <TabPanel value="2">
                            <Typography variant="h6" component="span">{t('installation.reactStep1')}</Typography>
                            <Box component="pre" sx={codeBlockSx}>
                                {
    `npm install visitorapi`
                                }
                            </Box>
                            <Typography variant="h6" component="span">{t('installation.step2Call')}</Typography>
                            <p>{t('installation.paramsIntro')}</p>
                            <ul>
                                <li>{t('installation.paramProjectId')} <b><i>{userData.currentAccount.id}</i></b></li>
                                <li>{t('installation.paramSuccess')}</li>
                                <li>{t('installation.paramError')}</li>
                            </ul>
                            <p>{t('installation.reactExampleIntro')}</p>
                            <Box component="pre" sx={codeBlockSx}>
                                {
    `const VisitorAPI = require("visitorapi");

    const [visitorData, setVisitorData] = useState({}); // store the whole json

    const [ipAddress, setIpAddress] = useState("");

    const [countryCode, setCountryCode] = useState("");
    const [countryName, setCountryName] = useState("");
    const [state, setState] = useState("");
    const [city, setCity] = useState("");
    const [cityLatLong, setCityLatLong] = useState(""); // city center latitude and longitude

    const [currencies, setCurrencies] = useState([]); // currencies data is an array
    const [languages, setLanguages] = useState([]); // languages data is an array

    const [browser, setBrowser] = useState("");
    const [browserVersion, setBrowserVersion] = useState("");

    const [deviceBrand, setDeviceBrand] = useState("");
    const [deviceModel, setDeviceModel] = useState("");
    const [deviceFamily, setDeviceFamily] = useState("");

    const [os, setOs] = useState("");
    const [osVersion, setOsVersion] = useState("");

    useEffect(() => {
    VisitorAPI(
        "`+userData.currentAccount.id+`",
        data => {
        setVisitorData(data);
        setIpAddress(data.ipAddress);
        setCountryCode(data.countryCode);
        setCountryName(data.countryName);
        setState(data.region);
        setCity(data.city);
        setCityLatLong(data.cityLatLong);
        setCurrencies(data.currencies);
        setLanguages(data.languages);
        setBrowser(data.browser);
        setBrowserVersion(data.browserVersion);
        setDeviceBrand(data.deviceBrand);
        setDeviceModel(data.deviceModel);
        setDeviceFamily(data.deviceFamily);
        setOs(data.os);
        setOsVersion(data.osVersion);
        }
    );
    },[]);`
                                }
                            </Box>
                            <p>{t('installation.docsLink')} <a href="https://www.visitorapi.com/help-articles/react" rel="noreferrer" target="_blank">documentation</a></p>
                        </TabPanel>
                        <TabPanel value="3">
                            <Typography variant="h6" component="span">{t('installation.gtmStep1')}</Typography>
                            <p>{t('installation.gtmImportInstructions')}</p>
                            <Typography variant="h6" component="span">{t('installation.gtmStep2')}</Typography>
                            <p>{t('installation.gtmCreateTagInstructions')}</p>
                            <p>{t('installation.gtmProjectIdInstructionsPrefix')} <b><i>{userData.currentAccount.id}</i></b> {t('installation.gtmProjectIdInstructionsSuffix')}</p>
                            <Typography variant="h6" component="span">{t('installation.gtmStep3')}</Typography>
                            <p>{t('installation.gtmVariablesInstructions')}</p>
                            <Typography variant="h6" component="span">{t('installation.gtmStep4')}</Typography>
                            <p>{t('installation.gtmTriggersInstructions')}</p>
                            <p>{t('installation.docsLink')} <a href="https://www.visitorapi.com/help-articles/google-tag-manager" rel="noreferrer" target="_blank">documentation</a></p>
                        </TabPanel>
                    </TabContext>
                </Box>
            </CardContent>
        </Card>
    )
}

export default Installation;
